import { SYSTEM_MODES } from "src/stores/slices/createAppSlice";
import { BUILD_STATUSES, ASSISTANT_TYPES } from "@consts";
import { isPhoneAssistant } from "@utils";
import useAppStore from "src/stores/appStore";
import { isAssistantPreviewableSelector, assistantIdSelector, selectedAssistantSelector, pageNameSelector } from "src/stores/selectors/appSelectors";
import { isUpdatingCollectionSelector } from "src/stores/selectors/collectionsSelectors";
import { hasFailedPollingDraftStatusSelector, previewLiveBuildResponseSelector, previewDraftBuildResponseSelector, previewLiveStatusPollingInProgressSelector, pollPreviewStatusSelector } from "src/stores/selectors/previewSelectors";
import { useDraft } from "src/queries/hooks/assistants";
import { useState, useEffect } from "react";
import { post } from "src/services/api";
import { queryClient } from "src/lib/react-query/queryClient";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { DONT_SHOW_AGAIN_STORAGE } from "../../header-types/status-header/DraftPublishing/DraftHeaderConsts";
import { setInfoPopoversSelector } from "src/stores/selectors/tripletsSelectors";
import { PREVIEW_BUTTON_TEXT, PREVIEW_TOOLTIP } from "./PreviewButton.consts";
import { useFetchDashboardSettings } from "src/queries/hooks/settings";

export const usePreviewButton = () => {
  const assistant = useAppStore(selectedAssistantSelector);
  const assistantId = useAppStore(assistantIdSelector);
  const systemMode = useAppStore((state) => state.systemMode);
  const { data: draft, isLoading: isLoadingDraft } = useDraft();
  const isFirstDraft = draft?.isFirstDraft;
  const isAssistantPreviewable = useAppStore(isAssistantPreviewableSelector);
  const previewLiveStatusPollingInProgress = useAppStore(previewLiveStatusPollingInProgressSelector);
  const isUpdatingCollection = useAppStore(isUpdatingCollectionSelector);
  const previewLiveBuildResponse = useAppStore(previewLiveBuildResponseSelector);
  const previewDraftBuildResponse = useAppStore(previewDraftBuildResponseSelector);
  const hasFailedPollingDraftStatus = useAppStore(hasFailedPollingDraftStatusSelector);
  const pollPreviewStatus = useAppStore(pollPreviewStatusSelector);
  const setInfoPopovers = useAppStore(setInfoPopoversSelector);
  const pageName = useAppStore(pageNameSelector);
  const assistantsToDisablePreview = import.meta.env.VITE_ASSISTANTS_TO_DISABLE_CHAT_PREVIEW?.split(",");
  const accountId = useAppStore(state => state.selectedAccountId);
  const dashboardSettings = useFetchDashboardSettings({ enabled: !!accountId })?.data;

  const [isBuilding, setIsBuilding] = useState(false);
  const isPhone = isPhoneAssistant(assistant?.channel);
  const isReadOnlyMode = systemMode === SYSTEM_MODES.READ_ONLY;
  const isEditMode = systemMode === SYSTEM_MODES.EDIT;
  const isAssistantNotLive = assistant?.type !== ASSISTANT_TYPES.LIVE;
  const isAssistantInDisableList = assistantId && assistantsToDisablePreview?.includes(assistantId);
  const hasNoPreviewBuildStatus = !previewLiveBuildResponse;
  const isDraftNotLive = draft && previewDraftBuildResponse?.status !== BUILD_STATUSES.LIVE;

  const isLoadingPreview = isAssistantNotLive && isReadOnlyMode && !isFirstDraft && (isLoadingDraft || hasNoPreviewBuildStatus);

  const shouldPreparePreview =
        isAssistantNotLive &&
        isReadOnlyMode &&
        !isFirstDraft &&
        ["", BUILD_STATUSES.TERMINATED, BUILD_STATUSES.FAILED].includes(previewLiveBuildResponse?.status);

  const isAssistantNotPreviewable = isAssistantNotLive &&
        isReadOnlyMode &&
        isFirstDraft;

  const isPreparingPreview = isAssistantNotLive &&
        !isFirstDraft &&
        isReadOnlyMode &&
        previewLiveStatusPollingInProgress &&
        previewLiveBuildResponse?.status === BUILD_STATUSES.PENDING;

  const hasEditModeRestrictions = isEditMode && (
    isDraftNotLive ||
        !draft ||
        hasFailedPollingDraftStatus
  );

  const hasReadOnlyModeRestrictions = isReadOnlyMode && isFirstDraft;

  const isPreviewDisabled = hasReadOnlyModeRestrictions ||
        isLoadingDraft ||
        hasEditModeRestrictions ||
        !isAssistantPreviewable ||
        isAssistantInDisableList ||
        isUpdatingCollection ||
        isPreparingPreview ||
        dashboardSettings?.disablePublish;

  useEffect(() => {
    if (previewLiveBuildResponse?.status === BUILD_STATUSES.PENDING) {
      setIsBuilding(false);
    }
  }, [previewLiveBuildResponse]);

  const getButtonText = () => {
    if (isPreparingPreview) {
      return PREVIEW_BUTTON_TEXT.PREPARING_PREVIEW;
    }

    return shouldPreparePreview ? PREVIEW_BUTTON_TEXT.PREPARE_PREVIEW : PREVIEW_BUTTON_TEXT.PREVIEW;
  };

  const handlePreparePreview = async () => {
    setIsBuilding(true);

    try {
      const endpoint = `/assistants/${assistant?.assistantId}/deploy/preview`;
      await post({
        endpoint,
        data: {},
        headers: { pageName }
      });

      queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.NOTIFICATIONS] });

      pollPreviewStatus({ isLivePreview: true });
    } catch (error) {
      console.error("Error occurred while preparing preview", error);
    }
  };

  const handlePreviewClick = (e, handleOpenDropdown) => {
    if (shouldPreparePreview) {
      handlePreparePreview();
      setInfoPopovers(DONT_SHOW_AGAIN_STORAGE.PREPARE_PREVIEW, true);
    } else {
      handleOpenDropdown(e);
    }
  };

  const getTooltipText = () => {
    if (!isPreviewDisabled) return "";
    if (isPreparingPreview) return PREVIEW_TOOLTIP.PREPARING_PREVIEW;
    if (isAssistantNotPreviewable) return PREVIEW_TOOLTIP.NOT_PREVIEWABLE;
    if (!draft || isUpdatingCollection) return PREVIEW_TOOLTIP.NO_CHANGES;
    if (draft && previewDraftBuildResponse?.status !== BUILD_STATUSES.LIVE) return PREVIEW_TOOLTIP.BUILD_NEEDED;
    return "";
  };

  return {
    isPreviewDisabled,
    shouldPreparePreview,
    isPreparingPreview,
    isLoadingPreview,
    isAssistantNotPreviewable,
    isPhone,
    isBuilding,
    setIsBuilding,
    getButtonText,
    handlePreparePreview,
    handlePreviewClick,
    getTooltipText
  };
};

